"use client";

import { c as _c } from "react/compiler-runtime";
import { queryOptions, useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { queryClient } from "@/config/react-query";
import type { AttachmentStudioState, PhotoStudioSettings, PhotoStudioSettingsUpdate, StudioBackdrop, StudioRender, StudioRenderSize } from "@/types/photo-studio";
import { getAttachmentStudio, getPhotoStudioSettings, getStudioRender, getStudioRenders, requestStudioRefine, revertAttachmentToOriginal, setAttachmentBackdrop, updatePhotoStudioSettings } from "./studio";

// Signed URLs from the render endpoints outlive this comfortably.
const RENDER_STALE_TIME = 20 * 60 * 1000;
const REFINE_POLL_INTERVAL = 3000;
// The batch endpoint makes only a few new thumbnails per request and marks the
// rest pending; ask again until none are.
const PENDING_THUMBNAIL_POLL_INTERVAL = 2000;
// The batch endpoint takes at most 100 photos.
const RENDER_BATCH_SIZE = 100;
export const photoStudioQueryKeys = {
  all: ["photo-studio"] as const,
  settings: () => [...photoStudioQueryKeys.all, "settings"] as const,
  attachment: (attachmentId: string) => [...photoStudioQueryKeys.all, "attachment", attachmentId] as const,
  renders: () => [...photoStudioQueryKeys.all, "render"] as const,
  render: (attachmentId: string, backdrop: StudioBackdrop | null, size: StudioRenderSize) => [...photoStudioQueryKeys.renders(), attachmentId, backdrop ?? "EFFECTIVE", size] as const,
  thumbnailBatches: () => [...photoStudioQueryKeys.all, "thumbnail-batch"] as const,
  thumbnailBatch: (attachmentIds: string[]) => [...photoStudioQueryKeys.thumbnailBatches(), attachmentIds] as const
};
function required<T>(value: T | null, message: string): T {
  if (value === null) throw new Error(message);
  return value;
}
export const photoStudioQueryOptions = {
  settings: () => queryOptions({
    queryKey: photoStudioQueryKeys.settings(),
    queryFn: async () => required(await getPhotoStudioSettings(), "Failed to load Studio settings"),
    staleTime: 5 * 60 * 1000
  }),
  attachment: (attachmentId: string) => queryOptions({
    queryKey: photoStudioQueryKeys.attachment(attachmentId),
    queryFn: async () => required(await getAttachmentStudio(attachmentId), "Failed to load Studio state")
  }),
  render: (attachmentId: string, backdrop: StudioBackdrop | null, size: StudioRenderSize) => queryOptions({
    queryKey: photoStudioQueryKeys.render(attachmentId, backdrop, size),
    queryFn: async () => required(await getStudioRender(attachmentId, {
      size,
      ...(backdrop ? {
        backdrop
      } : {})
    }), "Failed to load Studio photo"),
    staleTime: RENDER_STALE_TIME
  })
};
export function usePhotoStudioSettings(t0) {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = photoStudioQueryOptions.settings();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      ...t1,
      enabled
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}
export function useUpdatePhotoStudioSettings() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onMutate: _temp2,
      onError: _temp3,
      onSuccess: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp4(settings) {
  queryClient.setQueryData(photoStudioQueryKeys.settings(), settings);
  invalidateStudioRenders();
}
function _temp3(_error, _update, context) {
  if (context?.previous) {
    queryClient.setQueryData(photoStudioQueryKeys.settings(), context.previous);
  }
}
async function _temp2(update_0) {
  await queryClient.cancelQueries({
    queryKey: photoStudioQueryKeys.settings()
  });
  const previous = queryClient.getQueryData(photoStudioQueryKeys.settings());
  if (previous) {
    queryClient.setQueryData(photoStudioQueryKeys.settings(), {
      ...previous,
      ...update_0
    });
  }
  return {
    previous
  };
}
async function _temp(update) {
  return required(await updatePhotoStudioSettings(update), "Failed to save Studio settings");
}
export function invalidateStudioRenders(attachmentId?: string) {
  return Promise.all([queryClient.invalidateQueries({
    queryKey: attachmentId ? [...photoStudioQueryKeys.renders(), attachmentId] : photoStudioQueryKeys.renders()
  }), queryClient.invalidateQueries({
    queryKey: photoStudioQueryKeys.thumbnailBatches()
  })]);
}
export function setAttachmentStudioState(state: AttachmentStudioState) {
  queryClient.setQueryData(photoStudioQueryKeys.attachment(state.attachmentId), state);
}
const isRefining = (state: AttachmentStudioState | undefined) => state?.refineStatus === "PENDING" || state?.refineStatus === "RUNNING";

// Polls while the server is refining the cutout, and refreshes the Studio
// photo once the sharper mask lands.
export function useAttachmentStudio(attachmentId, t0) {
  const $ = _c(9);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== attachmentId) {
    t1 = photoStudioQueryOptions.attachment(attachmentId ?? "");
    $[0] = attachmentId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = enabled && !!attachmentId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2,
      refetchInterval: _temp5
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const query = useQuery(t3);
  const maskKey = query.data ? `${query.data.gcsPath}|${query.data.maskUpdatedAt ?? ""}` : null;
  const previousMaskKey = useRef(null);
  let t4;
  let t5;
  if ($[5] !== attachmentId || $[6] !== maskKey) {
    t4 = () => {
      if (!attachmentId || maskKey === null) {
        return;
      }
      const previous = previousMaskKey.current;
      if (previous?.id === attachmentId && previous.key !== maskKey) {
        invalidateStudioRenders(attachmentId);
      }
      previousMaskKey.current = {
        id: attachmentId,
        key: maskKey
      };
    };
    t5 = [attachmentId, maskKey];
    $[5] = attachmentId;
    $[6] = maskKey;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  return query;
}
function _temp5(q) {
  return isRefining(q.state.data) ? REFINE_POLL_INTERVAL : false;
}
export function useStudioRender(attachmentId, options, t0) {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== attachmentId || $[1] !== options.backdrop || $[2] !== options.size) {
    t1 = photoStudioQueryOptions.render(attachmentId ?? "", options.backdrop ?? null, options.size ?? "FULL");
    $[0] = attachmentId;
    $[1] = options.backdrop;
    $[2] = options.size;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = enabled && !!attachmentId;
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
}

// Module-level so useQueries keeps the combined Map stable between renders.
function combineStudioThumbnails(results: {
  data?: StudioRender[];
}[]): Map<string, string> {
  const urls = new Map<string, string>();
  for (const result of results) {
    for (const render of result.data ?? []) {
      if (render.signedUrl) urls.set(render.attachmentId, render.signedUrl);
    }
  }
  return urls;
}

/**
 * Studio thumbnails for a page of photos: one batch request per 100 photos.
 * Returns attachment id -> signed URL, only for photos that have a Studio
 * version (the rest keep their own thumbnail).
 */
export function useStudioThumbnails(attachmentIds, t0) {
  const $ = _c(5);
  const enabled = t0 === undefined ? true : t0;
  let result;
  if ($[0] !== attachmentIds) {
    const unique = Array.from(new Set(attachmentIds));
    result = [];
    for (let i = 0; i < unique.length; i = i + RENDER_BATCH_SIZE, i) {
      result.push(unique.slice(i, i + RENDER_BATCH_SIZE));
    }
    $[0] = attachmentIds;
    $[1] = result;
  } else {
    result = $[1];
  }
  const batches = result;
  let t1;
  if ($[2] !== batches || $[3] !== enabled) {
    t1 = {
      queries: batches.map(ids => ({
        queryKey: photoStudioQueryKeys.thumbnailBatch(ids),
        queryFn: async () => (await getStudioRenders({
          attachmentIds: ids,
          size: "THUMB"
        })) ?? [],
        enabled,
        staleTime: RENDER_STALE_TIME,
        refetchInterval: _temp7
      })),
      combine: combineStudioThumbnails
    };
    $[2] = batches;
    $[3] = enabled;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQueries(t1);
}
function _temp7(query) {
  return query.state.data?.some(_temp6) ? PENDING_THUMBNAIL_POLL_INTERVAL : false;
}
function _temp6(render) {
  return render.pending;
}
export function useSetAttachmentBackdrop() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp8,
      onSuccess: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp9(state) {
  setAttachmentStudioState(state);
  invalidateStudioRenders(state.attachmentId);
}
async function _temp8(t0) {
  const {
    attachmentId,
    backdrop
  } = t0;
  return required(await setAttachmentBackdrop(attachmentId, backdrop), "Failed to change the backdrop");
}
export function useRequestStudioRefine() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10,
      onSuccess: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp11(state) {
  return setAttachmentStudioState(state);
}
async function _temp10(attachmentId) {
  return required(await requestStudioRefine(attachmentId), "Failed to sharpen the cutout");
}
export function useRevertAttachmentToOriginal() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp12,
      onSuccess: _temp13
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp13(_attachment, attachmentId_0) {
  queryClient.invalidateQueries({
    queryKey: photoStudioQueryKeys.attachment(attachmentId_0)
  });
  invalidateStudioRenders(attachmentId_0);
}
async function _temp12(attachmentId) {
  return required(await revertAttachmentToOriginal(attachmentId), "Failed to restore the original photo");
}